import {
    DownloadFileResponseDto,
    FileShareQuotaResponseDto,
    FileShareQuotaResponseRawDto,
    InitiateUploadRequestDto,
    InitiateUploadResponseDto,
    MoveFileRequestDto,
    SharedFile,
    SharedFileDto,
    toFileShareQuotaResponse,
    toSharedFile,
} from "domain/fileSharing";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { ParameterQuery } from "utils/commonFunctions";

const PATH_FILES = "/api/v1/files";

const MAX_TOTAL_UPLOAD_SIZE_BYTES = 1_024 * 1_024 * 1_024;

export { MAX_TOTAL_UPLOAD_SIZE_BYTES };

async function generateChecksumSha256(buffer: ArrayBuffer): Promise<string> {
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(hashBuffer))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
}

export { generateChecksumSha256 };

class FileSharingService {
    async listFiles(options?: {
        directoryPath?: string;
        userFilesOnly?: boolean;
        cursor?: string;
        abortController?: AbortController;
    }): Promise<{ files: SharedFile[]; cursor?: string }> {
        const path =
            PATH_FILES +
            new ParameterQuery()
                .add("directory_path", options?.directoryPath)
                .add("user_files", options?.userFilesOnly ? "true" : undefined)
                .add("cursor", options?.cursor)
                .createQueryString();

        const body = await apiGatewayService.invokeApi<null, { files: SharedFileDto[]; cursor?: string }>(
            path,
            "get",
            null,
            { apiType: ApiType.ARTHUR, abortSignal: options?.abortController?.signal }
        );
        return { files: body.files.map(toSharedFile), cursor: body.cursor };
    }

    async initiateUpload(
        request: InitiateUploadRequestDto,
        abortController?: AbortController
    ): Promise<InitiateUploadResponseDto> {
        return apiGatewayService.invokeApi<InitiateUploadRequestDto, InitiateUploadResponseDto>(
            `${PATH_FILES}/upload`,
            "post",
            request,
            { apiType: ApiType.ARTHUR, abortSignal: abortController?.signal }
        );
    }

    async uploadToS3(uploadUrl: string, buffer: ArrayBuffer, signal?: AbortSignal): Promise<void> {
        const response = await fetch(uploadUrl, {
            method: "PUT",
            body: buffer,
            signal,
        });
        if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
        }
    }

    async getDownloadUrl(uuid: string, abortController?: AbortController): Promise<DownloadFileResponseDto> {
        return apiGatewayService.invokeApi<null, DownloadFileResponseDto>(
            `${PATH_FILES}/${encodeURIComponent(uuid)}`,
            "get",
            null,
            { apiType: ApiType.ARTHUR, abortSignal: abortController?.signal }
        );
    }

    async deleteFile(uuid: string, abortController?: AbortController): Promise<void> {
        await apiGatewayService.invokeApi<null, void>(`${PATH_FILES}/${encodeURIComponent(uuid)}`, "delete", null, {
            apiType: ApiType.ARTHUR,
            abortSignal: abortController?.signal,
            emptyResponse: true,
        });
    }

    async moveFile(request: MoveFileRequestDto, abortController?: AbortController): Promise<SharedFile> {
        const dto = await apiGatewayService.invokeApi<MoveFileRequestDto, SharedFileDto>(
            `${PATH_FILES}/move`,
            "post",
            request,
            { apiType: ApiType.ARTHUR, abortSignal: abortController?.signal }
        );
        return toSharedFile(dto);
    }

    async renameFile(request: MoveFileRequestDto, abortController?: AbortController): Promise<SharedFileDto> {
        return apiGatewayService.invokeApi<MoveFileRequestDto, SharedFileDto>(`${PATH_FILES}/move`, "post", request, {
            apiType: ApiType.ARTHUR,
            abortSignal: abortController?.signal,
        });
    }

    async fetchQuota(abortController?: AbortController): Promise<FileShareQuotaResponseDto> {
        const raw = await apiGatewayService.invokeApi<null, FileShareQuotaResponseRawDto>(
            `${PATH_FILES}/quota`,
            "get",
            null,
            {
                apiType: ApiType.ARTHUR,
                abortSignal: abortController?.signal,
            }
        );
        return toFileShareQuotaResponse(raw);
    }
}

export const fileSharingService = new FileSharingService();
export { FileSharingService };
